<template>
  <div class="container">
      <LoadingSpinner :loading="loading"> </LoadingSpinner>

      <div class="mt-4 mb-5" style="max-width:700px">
        <h3>Krediter Unimicro dagsoppgjør-bilag</h3>

        <div v-show="!loading">
            <div v-if="locations.length === 0" class="mt-4">
              <span class="message-info">Ingen avdelinger har Unimicro aktivert. Aktiver det under Avdelinger.</span>
            </div>

            <div v-else-if="!configured" class="mt-4">
              <span class="message-info">
                {{ stateMessage }}
                <template v-if="stateShowLink">Gå til <a href="/unimicro">Unimicro innstillinger</a>.</template>
              </span>
            </div>

            <div v-if="configured">
                <div v-if="hasMultiLocations()" class="mt-4">
                  <span class="ingredient-title">Velg avdeling</span>
                  <select class="form-select" v-model="locationId" @change="onLocationChange()">
                    <option v-for="(location, index) in locations" :key="index" :value="location.id">{{ location.name }}</option>
                  </select>
                </div>

                <div class="mt-4">
                  <p class="text-muted">Søk etter dagsoppgjør-bilag i Unimicro for en periode. Du kan se bilagene før du velger å kreditere.</p>
                  <SelectDate ref="selectDateFrom" label="Velg fra og med dato"/>
                  <SelectDate ref="selectDateTo" label="Velg til og med dato"/>
                  <button type="button" class="bo_btn mt-2" @click="searchJournalEntries"> Søk bilag </button>

                  <!-- Search results -->
                  <div v-if="searchCompleted" class="mt-3">
                    <div v-if="journalEntries.length === 0" class="message-info">
                      <p>{{ searchMessage }}</p>
                    </div>

                    <div v-else>
                      <p class="mb-2"><strong>{{ journalEntries.length }} dagsoppgjør-bilag funnet:</strong></p>
                      <table class="table table-sm table-bordered">
                        <thead>
                          <tr>
                            <th>Bnr.</th>
                            <th>Beskrivelse</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(v, index) in journalEntries" :key="index">
                            <td>{{ v.journalEntryNumber }}</td>
                            <td>{{ v.description }}</td>
                            <td><button type="button" class="btn btn-sm btn-outline-danger" @click="deleteSingleJournalEntry(v, index)">Krediter</button></td>
                          </tr>
                        </tbody>
                      </table>

                      <button type="button" class="bo_btn mt-2 btn-danger" @click="deleteJournalEntries">
                        Krediter {{ journalEntries.length }} bilag
                      </button>
                    </div>
                  </div>

                  <!-- Delete results -->
                  <div v-if="deleteCompleted" class="mt-3">
                    <div :class="deleteResult.success ? 'message-success' : 'message-error'">
                      <p>{{ deleteResult.message }}</p>
                      <p v-if="deleteResult.deletedCount !== undefined">Kreditert: {{ deleteResult.deletedCount }} av {{ deleteResult.totalFound }} | Feil: {{ deleteResult.errorCount }}</p>
                    </div>
                    <div v-if="deleteResult.errors" class="mt-2 message-error">
                      <span class="ingredient-title">Feildetaljer:</span>
                      <ul>
                        <li v-for="(error, index) in deleteResult.errors" :key="index">{{ error }}</li>
                      </ul>
                    </div>
                  </div>
                </div>
            </div>
        </div>
      </div>
    <ConfirmDialogue ref="confirmDialogue"/>
  </div>
</template>

<script>
  import LoadingSpinner from "@/components/other/LoadingSpinner";
  import SelectDate from "@/components/other/SelectDate";
  import ConfirmDialogue from "@/components/other/ConfirmDialogue";
  import SettingsService from "@/services/settings/SettingsService";
  import UnimicroState from "@/util/UnimicroState";
  import LocationUtil from "@/util/LocationUtil";
  import LocationService from "@/services/location/LocationService";

  export default {
    components: {LoadingSpinner, SelectDate, ConfirmDialogue},
    data() {
      return {
        loading: true,
        locationId: '',
        locations: [],
        configured: false,
        stateMessage: '',
        stateShowLink: false,
        searchCompleted: false,
        searchMessage: '',
        journalEntries: [],
        deleteCompleted: false,
        deleteResult: {}
      };
    },
    mounted() {
      LocationService.listLocations().then(
          (response) => {
            this.locations = response.data.filter(l => l.accountingSystem === 'UNIMICRO');
            this.locationId = this.locations.length ? this.locations[0].id : '';

            if (this.locationId) {
              this.checkAccountConfigured();
            } else {
              this.configured = false;
              this.loading = false;
            }
          },
          (error) => {
            console.log(error);
          }
      );
    },
    methods: {
      hasMultiLocations(){
        return LocationUtil.hasMultiLocations();
      },
      onLocationChange(){
        this.searchCompleted = false;
        this.deleteCompleted = false;
        this.journalEntries = [];
        this.checkAccountConfigured();
      },
      checkAccountConfigured(){
        UnimicroState.load(this.locationId).then((state) => {
          this.configured = state.active;
          this.stateMessage = state.message;
          this.stateShowLink = state.showLink;
          this.loading = false;
        });
      },
      searchJournalEntries(){
        this.loading = true;
        this.searchCompleted = false;
        this.deleteCompleted = false;
        this.journalEntries = [];
        let fromDate = this.$refs.selectDateFrom.getSelectedDate();
        let toDate = this.$refs.selectDateTo.getSelectedDate();

        SettingsService.searchUnimicroZReportJournalEntries(this.locationId, fromDate, toDate).then(
            (response) => {
              this.searchCompleted = true;
              this.journalEntries = response.data.entries || [];
              this.searchMessage = response.data.message || '';
              this.loading = false;
            },
            (error) => {
              this.searchCompleted = true;
              this.searchMessage = 'Feil ved søk etter bilag';
              console.log(error);
              this.loading = false;
            }
        );
      },
      async deleteSingleJournalEntry(journalEntry, index){
        const ok = await this.$refs.confirmDialogue.show({
          title: 'Krediter bilag',
          message: 'Er du sikker på at du vil kreditere bilag Bnr. ' + journalEntry.journalEntryNumber + ' (' + journalEntry.description + ')?',
          okButton: 'Krediter',
          cancelButton: 'Avbryt'
        });
        if (!ok) return;
        this.loading = true;
        this.deleteCompleted = false;

        SettingsService.deleteUnimicroSingleJournalEntry(this.locationId, journalEntry.id).then(
            (response) => {
              if (response.data.success) {
                this.journalEntries.splice(index, 1);
              } else {
                this.deleteCompleted = true;
                this.deleteResult = response.data;
              }
              this.loading = false;
            },
            (error) => {
              this.deleteCompleted = true;
              this.deleteResult = { success: false, message: 'Feil ved kreditering av bilag' };
              console.log(error);
              this.loading = false;
            }
        );
      },
      async deleteJournalEntries(){
        const ok = await this.$refs.confirmDialogue.show({
          title: 'Krediter bilag',
          message: 'Er du sikker på at du vil kreditere ' + this.journalEntries.length + ' dagsoppgjør-bilag fra Unimicro?',
          okButton: 'Krediter',
          cancelButton: 'Avbryt'
        });
        if (!ok) return;
        this.loading = true;
        this.deleteCompleted = false;
        let fromDate = this.$refs.selectDateFrom.getSelectedDate();
        let toDate = this.$refs.selectDateTo.getSelectedDate();

        SettingsService.deleteUnimicroZReportJournalEntries(this.locationId, fromDate, toDate).then(
            (response) => {
              this.deleteCompleted = true;
              this.deleteResult = response.data;
              this.searchCompleted = false;
              this.journalEntries = [];
              this.loading = false;
            },
            (error) => {
              this.deleteCompleted = true;
              this.deleteResult = { success: false, message: 'Feil ved kreditering av bilag' };
              console.log(error);
              this.loading = false;
            }
        );
      }
    }
  };
</script>
